interface MobileEnv {
  VITE_API_URL: string;
  VITE_FIREBASE_API_KEY: string;
  VITE_FIREBASE_AUTH_DOMAIN: string;
  VITE_FIREBASE_PROJECT_ID: string;
  VITE_FIREBASE_STORAGE_BUCKET: string;
  VITE_FIREBASE_MESSAGING_SENDER_ID: string;
  VITE_FIREBASE_APP_ID: string;
  VITE_FIREBASE_VAPID_KEY: string;
  VITE_SENTRY_DSN: string;
  VITE_SENTRY_ENVIRONMENT: string;
  VITE_SENTRY_RELEASE: string;
  /**
   * OTA manifest URL — only used by the Capacitor shell. Empty in regular web
   * builds (the workbox SW handles updates there).
   */
  VITE_OTA_MANIFEST_URL: string;
  /**
   * Build-time version stamp (git short SHA), injected by the
   * apps/mobile-native build:web script. Compared against the OTA manifest
   * to decide whether a downloaded bundle is actually newer than the one
   * currently running. Empty/'dev' for local web dev builds.
   */
  VITE_BUILD_VERSION: string;
  /**
   * ISO timestamp the bundle was built at (UTC). Used in the Settings
   * build line to show users / reviewers / support "Built 7 minutes ago"
   * — pairs with VITE_BUILD_VERSION for the full release identity. Empty
   * for local dev builds.
   */
  VITE_BUILD_TIMESTAMP: string;
}

/**
 * VITE_API_URL precedence:
 *   1. Vite build-time env (e.g. `VITE_API_URL=http://192.168.x.x:4000` when
 *      testing the native shell against a local API) — explicit wins.
 *   2. Runtime inference: native Capacitor shell → prod API; PWA on
 *      localhost/127/192.168.x → http://localhost:4000; everything else
 *      (mobile.underlings.com, etc.) → prod API.
 *
 *  We can't just check `hostname === 'localhost'` because Capacitor's
 *  Android shell loads the bundle from `https://localhost` and iOS uses
 *  `capacitor://localhost` — both would be misclassified as dev.
 */
function resolveApiUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof window === 'undefined') return 'http://localhost:4000';

  const { protocol, hostname } = window.location;
  const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  const isCapacitor = protocol === 'capacitor:' || cap?.isNativePlatform?.() === true;
  if (isCapacitor) return 'https://api.underlings.com';

  if (
    protocol === 'http:' &&
    (hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname))
  ) {
    return 'http://localhost:4000';
  }
  return 'https://api.underlings.com';
}

export const env: MobileEnv = {
  VITE_API_URL: resolveApiUrl(),
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
  VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  VITE_FIREBASE_VAPID_KEY: import.meta.env.VITE_FIREBASE_VAPID_KEY ?? '',
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN ?? '',
  VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? '',
  VITE_SENTRY_RELEASE: import.meta.env.VITE_SENTRY_RELEASE ?? '',
  VITE_OTA_MANIFEST_URL: import.meta.env.VITE_OTA_MANIFEST_URL ?? '',
  VITE_BUILD_VERSION: import.meta.env.VITE_BUILD_VERSION ?? 'dev',
  VITE_BUILD_TIMESTAMP: import.meta.env.VITE_BUILD_TIMESTAMP ?? '',
};
